import get from "lodash/get";
import PropTypes from "prop-types";

const canUseDOM = !!(typeof window !== "undefined" && window.document && window.document.createElement);

export const sharePageUrl = canUseDOM && window.location.href;

export function truncate(string = "", limit = 60, ellipsis = true) {
  const putEllipsis = ellipsis ? " ..." : "";
  return typeof string === "string" && string.length > limit ? string.substring(0, limit) + putEllipsis : string;
}

export const hexToRGB = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

export const getLuminanceOfColor = (hex) => {
  const { r, g, b } = hexToRGB(hex);
  return r * 0.299 + g * 0.587 + b * 0.114;
};

export const rgbToHex = (color = "rgba(255, 255, 255, 0)") => {
  const rgba = color.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
  return rgba && rgba.length === 4
    ? "#" +
        ("0" + parseInt(rgba[1], 10).toString(16)).slice(-2) +
        ("0" + parseInt(rgba[2], 10).toString(16)).slice(-2) +
        ("0" + parseInt(rgba[3], 10).toString(16)).slice(-2)
    : color;
};

const clientWidths = {
  mobile: 767,
  tablet: 1024,
};

export const clientWidth = (device) => {
  const foundDeviceWidth = get(clientWidths, device);
  if (foundDeviceWidth) {
    return get(global, "document.documentElement.clientWidth") <= foundDeviceWidth;
  }
  return false;
};

export const getTextColor = (theme = "rgb(255,255,255)") => {
  const isRegexCheck = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(theme);
  const getHexValue = !isRegexCheck ? rgbToHex(theme) : theme;
  const textColor = getLuminanceOfColor(getHexValue) < 155 ? "light" : "dark";
  return textColor;
};

export function getNumberOfStoriesToShow(numberOfStoriesToShow) {
  return numberOfStoriesToShow ? parseInt(numberOfStoriesToShow) : 1;
}

export const getSlot = (type, component, storyCallback, collectionCallback) => {
  switch (type) {
    case "story":
      return storyCallback();
    case "collection":
      return collectionCallback();
    default:
      return component ? component() : null;
  }
};

export const getSlug = (collection, config = {}) => {
  const { magazineSlug = "", isArchivePage = false } = config;
  if (isArchivePage) {
    return `/magazine/archive/${magazineSlug}`;
  }
  if (magazineSlug) {
    return `/magazine/${magazineSlug}/${collection.slug}`;
  }

  const isShortVideos = get(collection, ["associated-metadata", "is_short_videos"], false);
  if (isShortVideos) {
    return `/short-videos`;
  }

  const isSection = collection.template === "section";
  if (isSection) {
    const sectionId = get(collection, ["metadata", "section", "0", "id"], "");
    const { sections } = config;
    const section = sections && sections.find((section) => section.id === sectionId);
    const path = section ? new URL(section["section-url"])?.pathname : "/";

    return path;
  }
  return `/collection/${collection.slug}`;
};

export function isExternalStory(story = {}) {
  const { "story-template": storyTemplate = "", metadata = {} } = story;
  return storyTemplate === "news-elsewhere" ? get(metadata, ["reference-url"], "") : "";
}

// Adds target='_blank' attribute to anchor tags if not present
export const updateContentLinks = (content) => {
  let updatedContent = content;
  const anchorTagsList = content.match(/(<a).*?(>)/g);
  anchorTagsList &&
    anchorTagsList.forEach((anchorTag) => {
      const isTargetPresent = /target=/g.test(anchorTag);
      if (!isTargetPresent) {
        updatedContent = updatedContent.replace(
          anchorTag,
          `<a aria-label='content' target='_blank'${anchorTag.substring(2)}`
        );
      }
    });
  return updatedContent;
};

export const isEmpty = (value) => {
  return (
    value == null || // From standard.js: Always use === - but value == null is allowed to check null || undefined
    (typeof value === "object" && Object.entries(value).length === 0 && value.constructor === Object) ||
    (typeof value === "string" && value.trim().length === 0)
  );
};

export const shapeConfig = PropTypes.shape({
  "sketches-host": PropTypes.string,
  "cdn-name": PropTypes.string,
  "cdn-image": PropTypes.string,
});

export const shapeStory = PropTypes.shape({
  headline: PropTypes.string,
  "last-published-at": PropTypes.number,
  subheadline: PropTypes.string,
});

export const getStoryUrl = (story, defaultValue = "") => {
  return get(story, ["story-template"]) === "news-elsewhere"
    ? get(story, ["metadata", "reference-url"], "")
    : `/${story.slug}` || story.url || defaultValue;
};

export const detectComponent = (component, adComponent, widgetComp) => {
  const { type } = component;
  switch (type) {
    case "ad":
      return adComponent(component);
    case "widget":
      return widgetComp(component);
    default:
      return null;
  }
};

export const facebookMobileVideoResizeFix = () => {
  if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches) {
    const applyFixAfterTime = 3000;
    const aspectRatioAdjustmentFactor = 0.7;
    setTimeout(() => {
      document
        .querySelectorAll(".story-element-jsembed.story-element-jsembed-facebook-post span")
        .forEach((element) => {
          if (!element.classList.contains("fixed-mobile-fb-video")) {
            element.style.height = `${Math.ceil(element.offsetHeight * aspectRatioAdjustmentFactor)}px`;
            element.classList.add("fixed-mobile-fb-video");
          }
        });
    }, applyFixAfterTime);
  }
};

export const navigateTo = (dispatch, url = "") => {
  if (!url) {
    console.warn("navigateTo called without URL");
    return;
  }

  // Use window.location.href as the primary method
  try {
    window.location.href = url;
  } catch (error) {
    console.error("Error navigating to URL:", error);

    // Fallback to framework's navigation if available
    const navigateFn = global?.app?.navigateToPage;
    if (typeof navigateFn === "function" && dispatch) {
      try {
        navigateFn(dispatch, url);
      } catch (frameworkError) {
        console.error("Framework navigation also failed:", frameworkError);
      }
    }
  }
};

export const generateNavigateSlug = (collection = {}, config = {}, customUrlPath) => {
  if (customUrlPath) {
    return customUrlPath;
  }
  const { magazineSlug = "", isArchivePage = false } = config;
  if (isArchivePage) {
    return `/magazine/archive/${magazineSlug}`;
  }
  if (magazineSlug) {
    return `/magazine/${magazineSlug}/${collection.slug}`;
  }
  if (collection) {
    return getSlug(collection, config);
  }
  return "/";
};

export const getCollectionData = async (collectionSlug, mountAtPrefix = "") => {
  try {
    const result = await (
      await fetch(`${mountAtPrefix}/api/v1/collections/${collectionSlug}?item-type=story&limit=6`)
    ).json();
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
};

export function getAuthorTwitterUrl(author) {
  const twitterUrl = get(author, ["social", "twitter", "url"], null);
  const twitterHandle = get(author, ["social", "twitter", "handle"], null);

  if (!author) return "";

  if (twitterUrl) {
    return twitterUrl;
  }

  if (twitterHandle && twitterHandle.startsWith("@")) {
    return `https://www.twitter.com/${twitterHandle.slice(1)}`;
  }

  if (twitterHandle) {
    return `https://www.twitter.com/${twitterHandle}`;
  }

  return "";
}

export function removeHtmlTags(str) {
  if (str === null || str === "" || str === undefined) return false;
  else str = str.toString();

  return str.replace(/(<([^>]+)>)/gi, "");
}

export const isLeapYear = (year) => {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
};
